<template>
  <v-container>
    <loader :isLoading="isLoading"></loader>

    <form-component :formData="formData">
      <template #form-section>
        <v-card>
          <v-card-title>
            Forms
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              dark
              text
              class="mb-2"
              @click="onClickShowFormSelection()"
            >
              <v-icon>mdi-plus</v-icon>
              Add Form
            </v-btn>
          </v-card-title>
          <v-card-text>
            <ul style="list-style-type: none; margin: 0px; padding: 0px;">
              <draggable
                handle=".handle"
                :list="formSelectedTableMetadata.items"
              >
                <li
                  v-for="(item, index) in formSelectedTableMetadata.items"
                  :key="`pn${index}`"
                >
                  <v-btn text class="handle"><v-icon>mdi-menu</v-icon></v-btn>
                  <span class="text">{{ item.name }}</span>
                  <v-btn text class="close">
                    <v-icon
                      class="mr-2"
                      color="primary"
                      @click="onClickDeleteSelectedForm(item)"
                    >
                      mdi-delete
                    </v-icon>
                  </v-btn>
                  <v-btn
                    text
                    class="close"
                    @click="onClickViewFormPreview(item)"
                  >
                    <v-icon
                      class="mr-2"
                      color="blue"
                    >
                      mdi-eye
                    </v-icon>
                  </v-btn>
                </li>
              </draggable>
            </ul>
          </v-card-text>
        </v-card>
      </template>
      <template #project-code-alias-section>
        <v-card
          v-for="(al, index) in addedAliasList"
          :key="al.id"
          class="mb-4 pa-4"
          elevation="2"
          rounded="lg"
        >
          <!-- Header -->
          <div class="d-flex justify-space-between align-center mb-3">
            <div class="text-subtitle-1 font-weight-medium">
              Alias ({{ index+1 }})
            </div>

            <v-btn
              icon
              size="small"
              variant="text"
              color="grey-darken-1"
              @click="removeAlias(index, al.alias)"
            >
              <v-icon size="20">mdi-close</v-icon>
            </v-btn>
          </div>

          <!-- Ethics Number -->
          <v-text-field
            label="Project Code Alias"
            placeholder="Enter alias for project code"
            v-model="al.alias"
            :rules="[v => !!v || 'Required']"
            counter="100"
            maxlength="100"
            outlined
            dense
            clearable
            class="mb-3"
          />
        </v-card>
        <v-btn
          name="addButton"
          color="primary"
          class="mb-4"
          prepend-icon="mdi-plus"
          @click="addProjectCode()"
        >
          Add Project Code Alias
        </v-btn>
      </template>
      <template #default-ethics-section>
        <div class="default-ethics">
          <v-card
            class="mb-4 pa-4"
            elevation="2"
            rounded="lg"
          >
            <!-- Header -->
            <div class="d-flex justify-space-between align-center mb-3">
              <div class="text-subtitle-1 font-weight-medium">
                Ethics (If applicable)
              </div>
            </div>
            <v-text-field
              v-model="ethics.value"
              label="Ethics Number"
              placeholder="Enter ethics approval number"
              counter="100"
              maxlength="100"
              outlined
              dense
              clearable
              class="mb-3"
            />
            <v-row>
              <v-col cols="12" md="6">
                <date-picker
                  style="width: 100%;"
                  placeholder="Ethics Approved Date"
                  v-model="ethics.dateEthicsApproved"
                  :range="false"
                  :disabled="ethics.value? false : true"
                  :class="{ 'date-error': (
                    !ethics.dateEthicsApproved
                    && showEthicsErrors
                    && ethics.value)
                  }"
                />
              </v-col>

              <v-col cols="12" md="6">
                <date-picker
                  style="width: 100%;"
                  placeholder="Ethics Expiration Date"
                  v-model="ethics.dateExpiration"
                  :range="false"
                  :disabled="ethics.value? false : true"
                  :class="{ 'date-error':
                    !ethics.dateExpiration
                    && showEthicsErrors
                  }"
                />
              </v-col>
            </v-row>

          </v-card>
        </div>
      </template>
      <template #multiple-ethics-section>
        <div class="add-ethics">
          <v-card
            v-for="(ethics, index) in addedEthicsList"
            :key="ethics.id"
            class="mb-4 pa-4"
            elevation="2"
            rounded="lg"
          >
            <!-- Header -->
            <div class="d-flex justify-space-between align-center mb-3">
              <div class="text-subtitle-1 font-weight-medium">
                Ethics ({{ index+2 }})
              </div>

              <v-btn
                icon
                size="small"
                variant="text"
                color="grey-darken-1"
                @click="removeEthics(index, ethics.ethicsNumber)"
              >
                <v-icon size="20">mdi-close</v-icon>
              </v-btn>
            </div>

            <!-- Ethics Number -->
            <v-text-field
              :label="`Ethics Number`"
              placeholder="Enter ethics approval number"
              v-model="ethics.ethicsNumber"
              :rules="[v => !!v || 'Required']"
              counter="100"
              maxlength="100"
              outlined
              dense
              clearable
              class="mb-3"
            />

            <!-- Dates -->
            <v-row>
              <v-col cols="12" md="6">
                <date-picker
                  style="width: 100%;"
                  placeholder="Approved Date"
                  v-model="ethics.ethicsDateApproved"
                  :range="false"
                  :class="{ 'date-error': !ethics.ethicsDateApproved && showAddedEthicsErrors }"
                />
              </v-col>

              <v-col cols="12" md="6">
                <date-picker
                  style="width: 100%;"
                  placeholder="Expiration Date"
                  v-model="ethics.ethicsDateExpiration"
                  :range="false"
                  :class="{ 'date-error': !ethics.ethicsDateExpiration && showAddedEthicsErrors }"
                />
              </v-col>
            </v-row>
          </v-card>
          <v-btn
            name="addButtonEthics"
            color="primary"
            class="mb-4"
            prepend-icon="mdi-plus"
            @click="addEthicsNumber()"
          >
            Add Ethics
          </v-btn>
        </div>
      </template>
    </form-component>

    <v-navigation-drawer
      v-model="isSelectingForms"
      fixed
      right
      temporary
      width="650px"
    >
      <v-list-item>
        <h3>Select Form(s) to link</h3>
      </v-list-item>

      <v-divider></v-divider>

      <v-col cols="12">
        <v-card>
          <v-card-title>
            Forms
            <v-spacer></v-spacer>
            <v-text-field
              v-model="navigationDrawerFormSelectionMetadata.search"
              append-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
            ></v-text-field>
          </v-card-title>
          <v-data-table
            v-model="navigationDrawerFormSelectionMetadata.selected"
            :headers="navigationDrawerFormSelectionMetadata.headers"
            :items="navigationDrawerFormSelectionMetadata.items"
            :items-per-page="5"
            :search="navigationDrawerFormSelectionMetadata.search"
            item-key="_id"
            show-select
            class="elevation-1"
          >
            <template v-slot:[`item.actions`]="{ item }">
              <v-icon
                class="mr-2"
                @click="onClickViewFormPreview(item)"
                color="blue"
              >
                mdi-eye
              </v-icon>
            </template>
          </v-data-table>
        </v-card>
        <v-btn
          style="margin-top: 20px;"
          color="primary"
          @click="onClickAddSelectedForms"
        >
          Add
        </v-btn>

        <v-dialog v-model="isPreviewFormShown">
          <v-card>
            <v-card-title>
              Preview
            </v-card-title>
            <v-card-text>
              <template
                v-if="questionsMetadata.questionRawList && isPreviewFormShown"
              >
                <form-display
                  :questionList="questionsMetadata.questionRawList"
                  :formMetadata="formMetadata"
                >
                </form-display>
              </template>
            </v-card-text>
          </v-card>
        </v-dialog>
      </v-col>
    </v-navigation-drawer>
    <section v-if="formData.model">
      <v-col>
          <div class="float-right">
            <b>Created By:</b> {{createdBy.fullName}} <br>
            <b>Created At:</b> {{createdAt}}
          </div>
      </v-col>
    </section>
  </v-container>
</template>

<!-- eslint-disable no-underscore-dangle -->
<script>
import draggable from 'vuedraggable';
import DatePicker from 'vue2-datepicker';
import Loader from '../../../components/utilities/Loader.vue';
// import WidgetComponent from '../../../components/ui/WidgetComponent.vue';
import services from '../../../services';

import FormService from '../../../services/medical_registry/form.service';

import FormDisplay from '../form/display/FormDisplay.vue';

import FormComponent from '../../../components/ui/FormComponent.vue';

const { medicalRegistry, controlPanelRegistry, documentRegistry } = services;
const { formService, questionService, consentDocumentService } = medicalRegistry;
const { serviceAgreementService, permitService } = documentRegistry;
const { userService } = controlPanelRegistry;

export default {
  name: 'ProtocolNumberForm',
  components: {
    // WidgetComponent,
    draggable,
    FormDisplay,
    Loader,
    FormComponent,
    DatePicker,
  },
  props: {
    isLoading: Boolean,
    onSubmit: Function,
    protocolNumber: Object,
  },
  data() {
    const otherDocumentsId = this.protocolNumber?.otherDocuments?._id
      ? `${this.protocolNumber?.otherDocuments?._id}.pdf`
      : '';
    return {
      isSelectingForms: false,
      isPreviewFormShown: false,
      countAddedProjectCode: 0,
      ethicsObjectLocation: 6, // 13
      countEthics: 1,
      addedEthicsList: [],
      addedAliasList: [],
      createdBy: {},
      createdAt: null,
      showEthicsErrors: false,
      showAddedEthicsErrors: false,
      FormService,
      formMetadata: {
        _id: '',
        name: '',
        questions: [],
      },
      multiEthics: [],
      questionsMetadata: {
        questionRawList: [],
      },
      navigationDrawerFormSelectionMetadata: {
        search: '',
        selected: [],
        headers: [
          { text: 'Form Name', value: 'name' },
          { text: 'ID', value: '_id' },
          { text: 'Actions', value: 'actions', sortable: false },
        ],
        items: [],
      },
      userChoices: [],
      formSelectedTableMetadata: {
        search: '',
        headers: [
          { text: 'Form Name', value: 'name' },
          { text: 'ID', value: '_id' },
          { text: 'Actions', value: 'actions', sortable: false },
        ],
        items: [],
      },
      selectFormWidget: {
        type: 'select',
        name: 'type',
        label: 'Search forms',
        placeholder: 'Select a form',
        multiple: true,
        rules: [
          (v) => !!v || 'Question type is required',
        ],
        items: [],
        customFilter: (item, queryText) => {
          const label = item.label.toLowerCase();
          const searchText = queryText.toLowerCase();
          return label.indexOf(searchText) > -1;
        },
        value: '',
      },
      ethics: {
        value: '',
        dateEthicsApproved: null,
        dateExpiration: null,
      },
      formData: {
        onSubmit: this.onSave,
        model: this.protocolNumber,
        widgets: [
          {
            type: 'text',
            name: 'protocolNumber',
            label: 'Project Code',
            placeholder: 'Enter Project Code (e.g., In-house, Ethics)',
            value: '',
            rules: [
              (v) => !!v || 'Project Code is required',
              (v) => (v && v.length <= 100) || 'Project Code must be less than or equal to 100 characters',
            ],
          },
          {
            type: 'anchor',
            id: 'project-code-alias-section',
          },
          {
            type: 'date',
            name: 'dateApproved',
            label: 'Date Approved',
            placeholder: 'Define the approved date',
            rules: [
              (v) => !!v || 'Date approved is required',
            ],
            customStyle: {
              margin: '-10px 0px 20px 0px',
            },
            value: '',
            range: false,
          },
          {
            type: 'anchor',
            id: 'default-ethics-section',
          },
          {
            type: 'anchor',
            id: 'multiple-ethics-section',
          },
          {
            type: 'select',
            name: 'consentDocuments',
            label: 'Informed Consent (if applicable)',
            placeholder: 'Select your informed consent document',
            dense: true,
            chip: true,
            multiple: true,
            items: [],
            customFilter: (item, queryText) => {
              const label = item.label.toLowerCase();
              const searchText = queryText.toLowerCase();
              return label.indexOf(searchText) > -1;
            },
            value: '',
          },
          {
            type: 'select',
            name: 'permits',
            label: 'Permits (if applicable)',
            placeholder: 'Select your permits',
            dense: true,
            chip: true,
            multiple: true,
            items: [],
            customFilter: (item, queryText) => {
              const label = item.label.toLowerCase();
              const searchText = queryText.toLowerCase();
              return label.indexOf(searchText) > -1;
            },
            value: '',
          },
          {
            type: 'select',
            name: 'serviceAgreements',
            label: 'Service Agreement (if applicable)',
            placeholder: 'Select your service agreement documents',
            dense: true,
            chip: true,
            multiple: true,
            items: [],
            customFilter: (item, queryText) => {
              const label = item.label.toLowerCase();
              const searchText = queryText.toLowerCase();
              return label.indexOf(searchText) > -1;
            },
            value: '',
          },
          {
            type: 'file',
            name: 'otherDocuments',
            // eslint-disable-next-line no-underscore-dangle
            label: otherDocumentsId,
            placeholder: 'Upload Additional Documents (if applicable)',
            multiple: false,
            accept: '.pdf',
            // value: null,
          },
          {
            type: 'select',
            name: 'principalInvestigator',
            label: 'Principal Investigator',
            placeholder: 'Select the principal investigator',
            rules: [
              (v) => !!v || 'Principal Investigator is required',
            ],
            items: [],
            value: '',
          },
          {
            type: 'text',
            name: 'organizations',
            label: 'Organization',
            placeholder: 'Insert Organization',
            dense: true,
            chip: true,
            rules: [
              (v) => !!v || 'Organization is required',
            ],
            value: '',
          },
          {
            type: 'text',
            name: 'department',
            label: 'Department',
            placeholder: 'Insert Department',
            dense: true,
            chip: true,
            value: '',
          },
          {
            type: 'select',
            name: 'researchers',
            label: 'Researcher',
            placeholder: 'Select the researcher',
            dense: true,
            chip: true,
            multiple: true,
            rules: [
              (v) => !!v || 'Researcher is required',
            ],
            items: [],
            customFilter: (item, queryText) => {
              const label = item.label.toLowerCase();
              const searchText = queryText.toLowerCase();
              return label.indexOf(searchText) > -1;
            },
            value: '',
          },
          {
            type: 'select',
            name: 'collaborators',
            label: 'Collaborator',
            placeholder: 'Select the researcher',
            dense: true,
            chip: true,
            multiple: true,
            rules: [
              // (v) => !!v || 'Department is required',
            ],
            items: [],
            customFilter: (item, queryText) => {
              const label = item.label.toLowerCase();
              const searchText = queryText.toLowerCase();
              return label.indexOf(searchText) > -1;
            },
            value: '',
          },
          {
            type: 'anchor',
            id: 'form-section',
          },
          {
            type: 'button',
            btnType: 'submit',
            name: 'save',
            label: 'Save',
            color: 'primary',
            value: null,
            customStyle: {
              marginTop: '10px',
            },
            onClick: async () => {
              this.onSave();
            },
          },
          // {
          //   type: 'number',
          //   name: 'timePoints',
          //   label: 'Time Points',
          //   value: 1,
          //   placeholder: '1, 2, 3, ...',
          //   counter: 250,
          //   maxLength: 250,
          //   rules: [
          //     (v) => !!v || 'Box location is required',
          //   ],
          // },
        ],
      },
    };
  },
  async created() {
    this.questionsMetadata.questionRawList = await questionService.getList();
    this.navigationDrawerFormSelectionMetadata.items = await formService.getList();
    this.userChoices = await userService.getList();

    const consentDocumentWidget = this.formData.widgets.find((w) => w.name === 'consentDocuments');
    consentDocumentWidget.items = (await consentDocumentService.getList())
      .map((document) => ({ code: document._id, label: document.name }));
    const serviceAgreementWidget = this.formData.widgets.find((w) => w.name === 'serviceAgreements');
    serviceAgreementWidget.items = (await serviceAgreementService.getList())
      .map((document) => ({ code: document._id, label: document.name }));

    const permitWidget = this.formData.widgets.find((w) => w.name === 'permits');
    permitWidget.items = (await permitService.getList())
      .map((document) => ({ code: document._id, label: document.name }));

    if (this.formData?.model) {
      this.formData.widgets.forEach((w) => {
        const widget = w;
        const getValue = (key) => {
          if (!key) return undefined;

          if (typeof key === 'string' && key.includes('date')) {
            return this.formData.model[key]
              ? new Date(this.formData.model[key])
              : null;
          }

          return this.formData.model[key];
        };
        widget.value = getValue(widget.name);
      });

      // fetch the multiple ethics data
      const {
        additionalEthicsNumbers,
        ethicsNumber,
        dateEthicsApproved,
        dateExpiration,
        additionalProjectCodes,
      } = this.formData?.model;

      if (additionalEthicsNumbers.length) {
        this.addedEthicsList = additionalEthicsNumbers.map((e) => ({
          ...e,
          ethicsDateApproved: new Date(e.ethicsDateApproved),
          ethicsDateExpiration: new Date(e.ethicsDateExpiration),
        }));
      }

      if (additionalProjectCodes.length) {
        this.addedAliasList = additionalProjectCodes;
      }

      if (ethicsNumber) {
        this.ethics = {
          value: ethicsNumber,
          dateEthicsApproved: new Date(dateEthicsApproved),
          dateExpiration: new Date(dateExpiration),
        };
      }

      // prepare previously selected medical forms
      this.formData.model.medicalForms.forEach((formId) => {
        const form = this.navigationDrawerFormSelectionMetadata
          .items
          .find((f) => f._id === formId);
        this.formSelectedTableMetadata.items.push(form);
      });

      this.createdBy = this.formData.model._metadata.created.user;
      const formatCreatedDate = new Date(this.formData.model._metadata.created.date);
      this.createdAt = `${formatCreatedDate.toDateString()} ${formatCreatedDate.toLocaleTimeString()}`;
    }
    const principalInvestigator = this.formData.widgets.find((w) => w.name === 'principalInvestigator');
    principalInvestigator.items = this.userToChoices();

    const researcher = this.formData.widgets.find((w) => w.name === 'researchers');
    researcher.items = this.userToChoices();

    const collaborator = this.formData.widgets.find((w) => w.name === 'collaborators');
    collaborator.items = this.userToChoices();
  },
  methods: {
    userToChoices() {
      return this.userChoices.map(
        (user) => ({
          code: user._id,
          label: `${user.lastName}, ${user.firstName} ${user.middleName}`.trim(),
        }),
      );
    },
    getFormDataJSON() {
      const data = FormService.dataAsJSON(this.formData);
      // eslint-disable-next-line no-underscore-dangle
      data.medicalForms = this.formSelectedTableMetadata.items.map((f) => f._id);
      return data;
    },
    onSave() {
      // && this.formSelectedTableMetadata.items.length > 0
      const dataChanges = this.getFormDataJSON();
      this.showAddedEthicsErrors = false;
      this.showEthicsErrors = false;

      dataChanges.additionalProjectCodes = this.addedAliasList;
      console.log('dataChanges: ', dataChanges);
      const hasInvalidEthicsNumber = this.addedEthicsList.some((e) => (
        !e.ethicsNumber
        || !e.ethicsDateApproved
        || !e.ethicsDateExpiration
      ));

      const hasInvalidDefaultEthics = this.ethics.value
        && (!this.ethics.dateEthicsApproved
        || !this.ethics.dateExpiration);

      if (hasInvalidDefaultEthics) {
        this.showEthicsErrors = true;
        this.$snackbar.show({
          color: 'red',
          text: 'Ethics Date should be populated',
        });
        return;
      }

      if (hasInvalidEthicsNumber) {
        this.showAddedEthicsErrors = true;
        this.$snackbar.show({
          color: 'red',
          text: 'All added ethics field are required',
        });
        return;
      }

      dataChanges.additionalEthicsNumbers = this.addedEthicsList.map((e) => ({
        ...e,
        ethicsDateApproved: new Date(e.ethicsDateApproved),
        ethicsDateExpiration: new Date(e.ethicsDateExpiration),
      }));

      if (this.ethics.value) {
        dataChanges.ethicsNumber = this.ethics.value;
        dataChanges.dateEthicsApproved = this.ethics.dateEthicsApproved;
        dataChanges.dateExpiration = this.ethics.dateExpiration;
      }

      const data = Object.assign(this.protocolNumber || {}, dataChanges);
      this.onSubmit(data);
      // if (this.formSelectedTableMetadata.items.length === 0) {
      //   this.$confirmDialog.show({
      //     title: 'Save Error',
      //     text: `
      //       <ul>
      //         <li>Must have at least one medical form selected</li>
      //       </ul>
      //     `,
      //     isMessage: true,
      //   });
      // }
    },
    onClickShowFormSelection() {
      this.isSelectingForms = true;
    },
    onClickAddSelectedForms() {
      const selected = [...this.navigationDrawerFormSelectionMetadata.selected];
      this.navigationDrawerFormSelectionMetadata.selected = [];

      selected.forEach((form) => {
        const isPresent = this.formSelectedTableMetadata.items.find((f) => f._id === form._id);
        if (!isPresent) {
          this.formSelectedTableMetadata.items.push(form);
        }
      });
      this.isSelectingForms = false;
    },
    onClickDeleteSelectedForm(form) {
      this.$confirmDialog.show({
        text: `<p>Are you sure you want to delete: <b>${form.name}</b></p>`,
        title: 'Confirm Delete',
      }, () => {
        // eslint-disable-next-line no-underscore-dangle
        const filtered = this.formSelectedTableMetadata.items.filter((f) => f._id !== form._id);
        this.formSelectedTableMetadata.items = filtered;
        this.$snackbar.show({ color: 'green', text: 'Deleted successfully', closeColor: 'white' });
      });
    },
    onClickViewFormPreview(item) {
      this.isPreviewFormShown = true;
      this.formMetadata = item;
    },
    addProjectCode() {
      this.addedAliasList.push({
        id: Date.now() + Math.random(),
        alias: '',
      });
    },
    addEthicsNumber() {
      this.addedEthicsList.push({
        id: Date.now() + Math.random(),
        ethicsNumber: '',
        ethicsDateApproved: null,
        ethicsDateExpiration: null,
      });
    },
    removeEthics(index, ethicsNumber) {
      const label = ethicsNumber
        ? `Ethics No. ${ethicsNumber}`
        : 'this';
      this.$confirmDialog.show({
        title: 'Remove Ethics',
        text: `Are you sure you want to <b>remove</b> ${label}?`,
      }, async () => {
        this.addedEthicsList.splice(index, 1);
      });
    },
    removeAlias(index, alias) {
      const label = alias
        ? `${alias}`
        : 'this';
      this.$confirmDialog.show({
        title: 'Remove Alias',
        text: `Are you sure you want to <b>remove</b> ${label}?`,
      }, async () => {
        this.addedAliasList.splice(index, 1);
      });
    },
    toPickerDate(iso) {
      if (!iso) return null;
      return new Date(iso).toISOString().split('T')[0];
    },
    testData() {
      console.log('this.ethics test: ', this.ethics);
    },
  },
};
</script>

<style scoped>
.close {
  float: right;
  padding-top: 8px;
  padding-bottom: 8px;
}
.text {
  font-weight: bold;
  margin: 20px;
}
.v-card {
  transition: 0.2s ease;
}

.v-card:hover {
  transform: translateY(-2px);
}

.date-error {
  border: 1px solid red;
  border-radius: 4px;
}

</style>
